import React from "react";

import { civilValid, getCountriesList } from "../utils";
import { httpsCallable } from "firebase/functions";
import { useForm } from "@mantine/form";
import {

  Alert,
  Box,
  Button,
  Group,
  Radio,
  Select,
  SimpleGrid,
  Space,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";

import { IconAlertCircle } from "@tabler/icons-react";
import { AddClientRequest, BaseResult, CompanyRegistryData, CompanyRegistryLookupRequest, CompanyRegistryLookupResult, Customer, CustomerType, CustomClaims, OTHER_ID_TYPE_LABELS } from "../types";
import { useFunctions } from "../helpers/firebaseContext";
import { MyUserDataContext } from "../contexts";
import MobileCodeSelect, { describeInvalidMobile, dialCodeForSegment, isValidLocalMobile, KUWAIT_DIAL_CODE, lookupMobile, splitMobileWithDialCode } from "../components/mobile_code_select";


export interface RequestKYCUpdateProps {
  setModal?: (value: boolean) => void;
  onClose?: () => void;
  civilID?: string;
  customer?: Customer;
  isUpdate?: boolean;
}

type IndividualIDType = "civilID" | "other_id";
type MessageStatus = "success" | "warning" | "error";

const normalizeMobile = (mobile: string): string => {
  return (mobile || "").replace(/[\s()-]/g, "");
};


const INACTIVE_REGISTRY_STATUS_TERMS = [
  "موقوف",
  "موقوفة",
  "ملغي",
  "ملغية",
  "محظور",
  "محظورة",
  "مرهون",
  "مرهونة",
  "stopped",
  "suspended",
  "cancelled",
  "canceled",
  "blocked",
  "mortgaged",
];

const getCompanyRegistryWarnings = (companyData: CompanyRegistryData): string[] => {
  const warnings = [...(companyData.registry_warnings ?? [])];
  const statusCode = companyData.license_status?.trim();
  const statusDescription = companyData.license_status_desc?.trim();
  const hasInactiveStatus = (!!statusCode && statusCode !== "1")
    || (!!statusDescription && INACTIVE_REGISTRY_STATUS_TERMS.some((term) => statusDescription.toLowerCase().includes(term)));

  if (!warnings.length && hasInactiveStatus) {
    warnings.push(`MOCI returned company status ${statusDescription || "inactive"}${statusCode ? ` (code ${statusCode})` : ""}. Review before adding this company.`);
  }

  return [...new Set(warnings)];
};

export default function RequestKYCUpdate({ civilID, onClose, setModal, ...props }: RequestKYCUpdateProps) {
  const functions = useFunctions();
  const userContext = React.useContext(MyUserDataContext);
  const userClaims = (userContext.firebaseTokenResult?.claims.customClaims as CustomClaims | undefined) ?? {};
  // New clients default to the RM's country: "+20" for the KFH Egypt segment, "+965" otherwise.
  const defaultDialCode = dialCodeForSegment(userClaims.segment);
  const initialMobile = splitMobileWithDialCode(props.customer?.mobile ?? "", props.customer?.mobile_code ?? defaultDialCode);
  // const { setModal } = props;
  // const civilID = props?.civilID || "";
  // const onClose = props?.onClose;
  civilID = civilID || "";
  const [pending, setPending] = React.useState(false);
  const [lookupPending, setLookupPending] = React.useState(false);

  const [msg, setMsg] = React.useState(null as any);
  const [isError, setIsError] = React.useState(false);
  const [messageStatus, setMessageStatus] = React.useState<MessageStatus>("success");
  const [customerType, setCustomerType] = React.useState(props.customer?.customerType ?? "individual" as CustomerType);
  const [individualIdType, setIndividualIdType] = React.useState<IndividualIDType>(
    props.customer?.civilID ? "civilID" : props.customer?.other_id ? "other_id" : "civilID",
  );

  const form = useForm<AddClientRequest>({
    initialValues: {
      name_en: props.customer?.name_en ?? "",
      name_ar: props.customer?.name_ar ?? "",
      mobile: initialMobile.local,
      mobile_code: initialMobile.dialCode,
      email: props.customer?.email ?? "",
      civilID: props.customer?.civilID ?? "",
      other_id: props.customer?.other_id ?? "",
      other_id_type: "national_id",
      other_id_country: props.customer?.other_id_country ?? "",
      commercial_register: props.customer?.commercial_register ?? "",
      nationality: props.customer?.nationality ?? (defaultDialCode === KUWAIT_DIAL_CODE ? "KW" : "EG"),
      gender: props.customer?.gender ?? "",
      customerType: props.customer?.customerType ?? "individual"
    },

    validate: {
      name_en: (value) =>
        (customerType === "company" && !value.trim()) || value.length >= 2 ? null : "Name must have at least 2 letters",
      name_ar: (value) =>
        (customerType === "company" && !value.trim()) || value.length >= 2 ? null : "Name must have at least 2 letters",
      email: (value, values) => {
        // Clients without a Kuwait Civil ID sign through DocuSign, which is delivered by e-mail.
        if (values.customerType === "individual" && individualIdType === "other_id" && !value.trim()) {
          return "E-mail is required: clients without a Kuwait Civil ID sign via DocuSign (e-mail)";
        }
        if (value.length === 0) return null;
        return (/^\S+@\S+$/.test(value) ? null : "Invalid email");
      },
      mobile: (value, values) => {
        if (customerType === "company" && !value.trim()) return null;
        const dialCode = values.mobile_code || defaultDialCode;
        // a "+"-prefixed number typed into the mobile box is split on submit; validate what it resolves to
        const { dialCode: resolvedCode, local } = splitMobileWithDialCode(value, dialCode);
        // shape check only; the real check is Twilio Lookup on submit
        if (isValidLocalMobile(local, resolvedCode)) return null;
        return `Enter the local number digits only (country code ${resolvedCode} is selected separately)`;
      },
      civilID: (value) => ((customerType == "individual" && individualIdType == "civilID" && (!value || !civilValid(value))) ? "Invalid civil ID" : null),
      other_id: (value, values) => {
        if (customerType !== "individual" || individualIdType !== "other_id") return null;
        if (!value?.trim()) return "ID is mandatory";
        // Kuwaiti nationals authenticate with PACI, which requires the Kuwait Civil ID.
        if (values.nationality === "KW") return "Kuwaiti clients must be registered with their Kuwait Civil ID";
        return null;
      },
      nationality: (value) => {
        if (customerType === "company" && !value.trim()) return null;
        return getCountriesList().filter((country) => { return country.value === value }).length <= 0 ? "Nationality is mandatory" : null;
      },
      other_id_country: (value) => {
        if (customerType !== "individual" || individualIdType !== "other_id") return null;
        if (!value || !getCountriesList().some((country) => country.value === value)) return "Select the country that issued the national ID";
        if (value === "KW") return "A Kuwait-issued ID is the Civil ID: use the Kuwait Civil ID option";
        return null;
      },
      gender: (value) => ((value !== "M" && value !== "F" && customerType == "individual") ? "Gender is mandatory" : null),
    },
  });
  const lookupCompanyInfo = async () => {
    const commercialRegister = form.values.commercial_register?.trim();
    if (!commercialRegister) {
      setIsError(true);
      setMessageStatus("error");
      setMsg("Commercial Register is required.");
      return;
    }

    setLookupPending(true);
    setMsg(null);
    setMessageStatus("success");
    try {
      const lookupCompanyFunction = httpsCallable<CompanyRegistryLookupRequest, CompanyRegistryLookupResult>(functions, "lookupCompanyRegistry");
      const result = await lookupCompanyFunction({ commercial_register: commercialRegister });
      const companyData = result.data?.companyData;

      if (!companyData) {
        throw new Error("No company data was returned.");
      }

      form.setFieldValue("commercial_register", companyData.commercial_register || commercialRegister);
      form.setFieldValue("name_ar", companyData.name_ar || form.values.name_ar);
      form.setFieldValue("name_en", companyData.name_en || form.values.name_en);
      form.setFieldValue("nationality", companyData.nationality || form.values.nationality || "KW");
      form.setFieldValue("mobile", companyData.mobile || form.values.mobile);
      form.setFieldValue("email", companyData.email || form.values.email);

      const registryWarnings = getCompanyRegistryWarnings(companyData);
      setIsError(false);
      if (registryWarnings.length) {
        setMessageStatus("warning");
        setMsg(<>
          Company data fetched from MOCI, but review the registry status before adding.
          <ul>
            {registryWarnings.map((warning) => <li key={warning}>{warning}</li>)}
          </ul>
        </>);
      } else {
        setMessageStatus("success");
        setMsg(<>Company data fetched from MOCI.</>);
      }
    } catch (error: any) {
      setIsError(true);
      setMessageStatus("error");
      setMsg(error.message);
    } finally {
      setLookupPending(false);
    }
  };
  const handleSubmit = async (values: Omit<AddClientRequest, "id">, event: any) => {
    event.preventDefault();
    setPending(true);
    let payload: AddClientRequest = { ...values, isUpdate: props.isUpdate };
    // Stored shape: local digits in `mobile`, dial code in `mobile_code`.
    const mobileParts = splitMobileWithDialCode(normalizeMobile(payload.mobile ?? ""), payload.mobile_code || defaultDialCode);
    payload.mobile = mobileParts.local;
    payload.mobile_code = mobileParts.local ? mobileParts.dialCode : undefined;
    payload.email = payload.email?.trim();

    // Twilio Lookup validates the number for any country. An explicit "invalid" blocks the
    // submit; a lookup outage does not (the backend repeats the check anyway).
    if (payload.mobile) {
      const lookup = await lookupMobile(functions, payload.mobile, payload.mobile_code);
      if (lookup.valid === false) {
        form.setFieldError("mobile", describeInvalidMobile(lookup));
        setPending(false);
        return;
      }
    }

    if (payload.customerType === "individual") {
      delete payload.commercial_register;
      if (individualIdType === "civilID") {
        payload.civilID = payload.civilID?.trim();
        delete payload.other_id;
        delete payload.other_id_type;
        delete payload.other_id_country;
      } else {
        payload.other_id = payload.other_id?.trim();
        payload.other_id_type = "national_id";
        payload.other_id_country = payload.other_id_country?.trim().toUpperCase();
        delete payload.civilID;
      }
    } else if (payload.customerType === "company") {
      payload.commercial_register = payload.commercial_register?.trim();
      delete payload.civilID;
      delete payload.other_id;
      delete payload.other_id_type;
      delete payload.other_id_country;
    }

    try {
      const addClientFunction = httpsCallable<AddClientRequest, BaseResult>(functions, "addClient");

      //set type to submit because we want the user to create an entirely new KYC
      await addClientFunction(payload);

      // e.target.reset();
      setMessageStatus("success");
      setMsg(<>{props.isUpdate ? "Client Updated successfully." : "Client Added successfully."}</>);
      setIsError(false);
      setTimeout(() => {
        if (onClose) {
          onClose()
        }
        if (setModal) {
          setModal(false)
        }
      }

        , 2000);
    } catch (error: any) {
      setIsError(true);
      setMessageStatus("error");
      console.log(error);
      setMsg(error.message);
    }

    setPending(false);
  };
  const alertColor = isError ? "red" : messageStatus === "warning" ? "yellow" : "green";
  const alertTitle = isError ? "Failure" : messageStatus === "warning" ? "Review company status" : "Success";

  return (
    <Stack>
      <form
        onSubmit={form.onSubmit((values, _event) =>
          handleSubmit(values, _event)
        )}
        onChange={() => {
          setMsg(null);
          setMessageStatus("success");
        }}
      >
        <SimpleGrid cols={1}>
          <Radio.Group
            name="customerType"
            label="Customer Type"

            withAsterisk
            {...form.getInputProps("customerType")}
            onChange={(value) => {
              setCustomerType(value as CustomerType);
              form.setFieldValue("customerType", value as CustomerType);
              form.setFieldValue("civilID", "");
              form.setFieldValue("other_id", "");
              form.setFieldValue("commercial_register", "");
            }}
          >
            <Group mt="xs">
              <Radio disabled={props.isUpdate} value="individual" label="Individual" />
              <Radio disabled={props.isUpdate} value="company" label="Company" />
            </Group>
          </Radio.Group>
          {customerType == "individual" && <>
            <Radio.Group
              name="individualIdType"
              label="ID Type"
              withAsterisk
              value={individualIdType}
              onChange={(value) => {
                setIndividualIdType(value as IndividualIDType);
                form.setFieldValue("civilID", "");
                form.setFieldValue("other_id", "");
              }}
            >
              <Group mt="xs">
                <Radio disabled={props.isUpdate} value="civilID" label="Kuwait Civil ID" />
                <Radio disabled={props.isUpdate} value="other_id" label="Other ID" />
              </Group>
            </Radio.Group>
            {individualIdType == "civilID" ? <TextInput
              label="Civil ID"
              disabled={props.isUpdate}
              placeholder="Enter Civil ID"
              {...form.getInputProps("civilID")}
            /> : <>
              <TextInput
                label={`${OTHER_ID_TYPE_LABELS.national_id} Number`}
                description="Permanent national ID number of the client's country. Passport numbers are not accepted as an identifier (they change on renewal); the passport copy is still uploaded as a KYC document."
                disabled={props.isUpdate}
                placeholder="Enter the national ID number"
                {...form.getInputProps("other_id")}
              />
              <Select
                label="Issuing country"
                description={props.isUpdate && !props.customer?.other_id_country
                  ? "This client was saved without an issuing country. Select the country that issued the national ID to continue."
                  : "Country that issued the national ID. National ID numbers are only unique within their country."}
                withAsterisk
                searchable
                allowDeselect={false}
                // The identity (number + issuing country) is fixed once saved; older records saved without a
                // country must be able to supply it on update, otherwise ordinary edits fail validation.
                disabled={props.isUpdate && !!props.customer?.other_id_country}
                data={getCountriesList().filter((country) => country.value !== "KW").map((country) => ({ value: country.value, label: `${country.emoji} ${country.label}` }))}
                {...form.getInputProps("other_id_country")}
              />
              <Text size="xs" c="dimmed">
                Clients without a Kuwait Civil ID cannot use PACI: their orders and portfolio requests are signed through DocuSign (sent by e-mail), so e-mail is mandatory.
              </Text>
            </>}
          </>}
          {customerType == "company" && <Group align="end" grow>
            <TextInput
              label="Commercial Register"
              disabled={props.isUpdate}
              placeholder="Enter Commercial Register"
              {...form.getInputProps("commercial_register")}
            />
            <Button
              type="button"
              loading={lookupPending}
              disabled={!form.values.commercial_register?.trim()}
              onClick={lookupCompanyInfo}
            >
              Fetch
            </Button>
          </Group>}
          {/* <SimpleGrid cols={2}> */}
          <TextInput
            // withAsterisk
            label="Name (English)"
            placeholder="Joe"
            {...form.getInputProps("name_en")}
          />
          <TextInput
            // withAsterisk
            label="Name (Arabic)"
            placeholder="جو"
            {...form.getInputProps("name_ar")}
          />
          {/* </SimpleGrid> */}
          <TextInput
            withAsterisk={customerType === "individual" && individualIdType === "other_id"}
            label="Email"
            placeholder="their@email.com"
            {...form.getInputProps("email")}
          />
          <Group align="start" wrap="nowrap" gap="xs">
            <MobileCodeSelect
              style={{ flex: "0 0 45%" }}
              value={form.values.mobile_code || defaultDialCode}
              onChange={(dialCode) => form.setFieldValue("mobile_code", dialCode)}
            />
            <TextInput
              style={{ flex: 1 }}
              label="Mobile"
              placeholder={(form.values.mobile_code || defaultDialCode) === KUWAIT_DIAL_CODE ? "e.g. 65123456" : "local number, e.g. 1012345678"}
              {...form.getInputProps("mobile")}
              onChange={(event) => {
                // typing a full "+20..." number moves the country code into the picker
                const typed = event.currentTarget.value;
                if (typed.trim().startsWith("+") || typed.trim().startsWith("00")) {
                  const parts = splitMobileWithDialCode(typed, form.values.mobile_code || defaultDialCode);
                  if (parts.matched) {
                    form.setFieldValue("mobile_code", parts.dialCode);
                    form.setFieldValue("mobile", parts.local);
                    return;
                  }
                }
                form.setFieldValue("mobile", typed);
              }}
            />
          </Group>
          <Select
            label="Nationality"
            searchable
            nothingFoundMessage="No country"
            data={getCountriesList().map((country) => ({ value: country.value, label: `${country.emoji} ${country.label}` }))}
            {...form.getInputProps("nationality")}
            onChange={(value) => {
              form.setFieldValue("nationality", value ?? "");
              // the national ID is normally issued by the client's own country; pre-fill, RM can override
              if (individualIdType === "other_id" && value && value !== "KW" && !form.values.other_id_country) {
                form.setFieldValue("other_id_country", value);
              }
            }}
          />

          {customerType == "individual" && <Radio.Group
            name="gender"
            label="Gender"
            {...form.getInputProps("gender")}
          >
            <Group mt="xs">
              <Radio value="M" label="Male" />
              <Radio value="F" label="Female" />

            </Group>
          </Radio.Group>}
        </SimpleGrid>

        <Box
          maw={300}
          mx="auto">
          <Space h="xl" />
          {!!msg ? ( //love my check if it's an error or not.  Please don't do this, it's just a proof of concept page.
            <Alert
              icon={<IconAlertCircle size={16} />}
              color={alertColor}
              title={alertTitle}
            >
              {msg}
            </Alert>
          ) : null}
          <Group justify="center" mt="md">
            <Button type="submit" loading={pending}>
              {props.isUpdate ? "Update Client" : "Add Client"}
            </Button>
          </Group>
        </Box>
      </form>
    </Stack>
  );
}
